// src/pages/PropertiesScreen.js

import React, { useState, useEffect, useMemo } from 'react';
import { PlusCircle, Edit, Search, DollarSign, Download, MoreVertical, UserPlus, Settings, LogIn } from 'lucide-react'; 
import { api } from '../api';
import toast from 'react-hot-toast';

// --- Style and Component Imports ---
import '../styles/PropertiesScreen.css';
import PropertyModal from '../components/properties/PropertyModal';
import AddServiceModal from '../components/properties/AddServiceModal';
import EditEmployeeModal from '../components/employees/EditEmployeeModal';
import AddEmployeeModal from '../components/employees/AddEmployeeModal';
import DeviceModal from '../components/devices/DeviceModal';
import RebootModal from '../components/devices/RebootModal';
import WiFiConfigModal from '../components/devices/WiFiConfigModal';
import SetTimeModal from '../components/devices/SetTimeModal';
import AccessTimeModal from '../components/devices/AccessTimeModal';
import NtpSettingsModal from '../components/devices/NtpSettingsModal';
import AddEmployeeByFPModal from '../components/devices/AddEmployeeByFPModal';
import AddEmployeeByFaceModal from '../components/devices/AddEmployeeByFaceModal';
import PropertySettingsTab from '../components/properties/PropertySettingsTab'; 

// --- Helper Component for Workforce ---
const RoleSection = ({ title, people, onPersonClick, currentUser, onImpersonate }) => {
    if (!people || people.length === 0) {
        return <div className="role-section empty"><h3 className="role-header">{title}</h3><p>No one assigned to this role.</p></div>;
    }

    // Helper: Only SuperAdmins can impersonate, but not other SuperAdmins
    const canImpersonate = (targetRole) => {
        return currentUser && currentUser.role === 'SuperAdmin' && targetRole !== 'SuperAdmin';
    };

    const canUpdate = (personRole) => {
        if (!currentUser || !currentUser.role) return false;
        if (currentUser.role === 'SuperAdmin') return true;
        if (currentUser.role === 'Owner') return ['Owner', 'Manager', 'Housekeeper'].includes(personRole);
        if (currentUser.role === 'Manager') return ['Manager', 'Housekeeper'].includes(personRole);
        return false;
    };

    return (
        <div className="role-section">
            <h3 className="role-header">{title}</h3>
            <div className="people-grid">
                {people.map(person => {
                    const isClickable = canUpdate(person.role);
                    const showImpersonate = canImpersonate(person.role);

                    return (
                        <div
                            key={`${title}-${person.id}`}
                            className={`person-card ${isClickable ? '' : 'disabled'}`}
                            // Only trigger Edit on main card click if NOT clicking a specific action button
                            onClick={(e) => {
                                // Prevent edit modal if clicking the impersonate button
                                if (e.target.closest('.impersonate-btn')) return;
                                if (isClickable) onPersonClick(person);
                            }}
                            style={{ position: 'relative' }} 
                        >
                            {/* --- IMPERSONATE BUTTON (Top Right) --- */}
                            {showImpersonate && (
                                <button
                                    className="impersonate-btn"
                                    onClick={(e) => {
                                        e.stopPropagation(); // Stop bubbling to card click
                                        onImpersonate(person);
                                    }}
                                    title={`Log in as ${person.first_name}`}
                                    style={{
                                        position: 'absolute',
                                        top: '8px',
                                        right: '8px',
                                        background: 'transparent',
                                        border: 'none',
                                        cursor: 'pointer',
                                        color: '#64748b',
                                        padding: '4px',
                                        borderRadius: '4px',
                                        transition: 'background 0.2s',
                                        zIndex: 10
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#0f172a'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#64748b'; }}
                                >
                                    <LogIn size={16} />
                                </button>
                            )}

                            <div className="person-avatar">{person.first_name.charAt(0)}{person.last_name.charAt(0)}</div>
                            <div className="person-details">
                                <div className="person-name">{person.first_name} {person.last_name}</div>
                                <div className="person-email">{person.email}</div>
                            </div>
                            <span className={`status-badge-dot ${person.is_active ? 'active' : 'inactive'}`}></span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// --- Main Properties Screen Component ---
const PropertiesScreen = ({ user }) => {
    const [properties, setProperties] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [activeTab, setActiveTab] = useState('workforce');
    const [sortOrder, setSortOrder] = useState('status');
    const [workforceData, setWorkforceData] = useState({ Owner: [], Manager: [], Accountant: [], Housekeeper: [] });
    const [isWorkforceLoading, setIsWorkforceLoading] = useState(false);
    const [devices, setDevices] = useState([]);
    const [isDevicesLoading, setIsDevicesLoading] = useState(false);
    
    // Modals & Menus
    const [isPropertyModalOpen, setIsPropertyModalOpen] = useState(false);
    const [propertyToEdit, setPropertyToEdit] = useState(null);
    const [isAddEmployeeModalOpen, setIsAddEmployeeModalOpen] = useState(false);
    const [isEditEmployeeModalOpen, setIsEditEmployeeModalOpen] = useState(false);
    const [selectedPerson, setSelectedPerson] = useState(null);
    const [isDeviceModalOpen, setIsDeviceModalOpen] = useState(false);
    const [deviceToEdit, setDeviceToEdit] = useState(null);
    const [serviceModalState, setServiceModalState] = useState({ isOpen: false, property: null });
    const [openActionMenu, setOpenActionMenu] = useState(null);
    const [openDeviceActionMenu, setOpenDeviceActionMenu] = useState(null);
    const [deviceActionState, setDeviceActionState] = useState({ type: null, device: null });
    const [isSubmittingDeviceAction, setIsSubmittingDeviceAction] = useState(false);

    // --- NEW: Impersonation Modal State ---
    const [impersonateTarget, setImpersonateTarget] = useState(null);

    const canManageProperties = user && user.role === 'SuperAdmin';
    const canDownload = user && ['SuperAdmin', 'Owner', 'Manager'].includes(user.role);
    const canViewActions = canManageProperties || canDownload;
    const canAddEmployee = user && ['SuperAdmin', 'Owner', 'Manager'].includes(user.role);
    const canManageSettings = user && ['SuperAdmin', 'Owner', 'Manager'].includes(user.role);

    const fetchProperties = async () => {
        setIsLoading(true);
        try {
            const propertiesData = await api.getProperties();
            setProperties(propertiesData || []);
            
            if (selectedProperty) {
                 const updatedSelected = propertiesData.find(p => p.id === selectedProperty.id);
                 if (updatedSelected) setSelectedProperty(updatedSelected);
            } else if (propertiesData && propertiesData.length > 0) {
                const sortedInitial = [...(propertiesData || [])].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                setSelectedProperty(sortedInitial[0]);
            }
        } catch (err) { setError(err.message); }
        finally { setIsLoading(false); }
    };

    useEffect(() => {
        if (user) fetchProperties();
    }, [user]);

    useEffect(() => {
        if (!selectedProperty || (activeTab !== 'workforce' && activeTab !== 'devices')) return;
        const loadTabData = async () => {
            if (activeTab === 'workforce') {
                setIsWorkforceLoading(true);
                try {
                    const flatList = await api.getWorkforce({ propertyId: selectedProperty.id });
                    const data = (flatList || []).reduce((acc, person) => {
                        const groupRole = person.role === 'SuperAdmin' ? 'Owner' : person.role;
                        if (!acc[groupRole]) acc[groupRole] = [];
                        acc[groupRole].push(person);
                        return acc;
                    }, { Owner: [], Manager: [], Accountant: [], Housekeeper: [] });
                    setWorkforceData(data);
                } catch (error) { toast.error(`Failed to load workforce: ${error.message}`); }
                finally { setIsWorkforceLoading(false); }
            } else if (activeTab === 'devices') {
                setIsDevicesLoading(true);
                try {
                    const devicesData = await api.getDevices({ propertyId: selectedProperty.id });
                    setDevices(devicesData || []);
                } catch (error) { toast.error(`Failed to load devices: ${error.message}`); }
                finally { setIsDevicesLoading(false); }
            }
        };
        loadTabData();
    }, [selectedProperty, activeTab]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openActionMenu && !event.target.closest('.action-menu-container')) setOpenActionMenu(null);
            if (openDeviceActionMenu && !event.target.closest('.action-menu-container')) setOpenDeviceActionMenu(null);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openActionMenu, openDeviceActionMenu]);

    const handleSelectProperty = (property) => { setSelectedProperty(property); setActiveTab('workforce'); setOpenActionMenu(null); };
    const handleDownloadAll = (propertyCode) => { toast.promise(api.downloadAllImageFiles(propertyCode), { loading: 'Preparing download...', success: 'Download started!', error: (err) => `Download failed: ${err.message}`, }); };
    const handleOpenAddPropertyModal = () => { setPropertyToEdit(null); setError(null); setIsPropertyModalOpen(true); };
    const handleOpenEditPropertyModal = (property) => { setPropertyToEdit(property); setError(null); setIsPropertyModalOpen(true); };
    const handleOpenServiceModal = (property) => { setServiceModalState({ isOpen: true, property: property }); };

    const handleSaveProperty = async (propertyData) => {
        const savePromise = propertyToEdit ? api.updateProperty(propertyToEdit.id, propertyData) : api.createProperty(propertyData);
        toast.promise(savePromise, {
            loading: 'Saving property...',
            success: () => { setIsPropertyModalOpen(false); setPropertyToEdit(null); fetchProperties(); return 'Property saved successfully!'; },
            error: (err) => `Error: ${err.message}`,
        });
    };

    const handleAddService = async (serviceData) => {
        try {
            await api.createSubscription(serviceData);
            toast.success('Service added successfully! Invoice created.');
        } catch (error) {
            toast.error(error.message || 'Failed to add service.');
        }
    };

    const handleOpenAddEmployeeModal = () => setIsAddEmployeeModalOpen(true);
    const handleOpenEditEmployeeModal = (person) => { setSelectedPerson(person); setIsEditEmployeeModalOpen(true); };
    const refreshWorkforce = () => { setActiveTab(''); setTimeout(() => setActiveTab('workforce'), 10); };
    const handleOpenAddDeviceModal = () => { setDeviceToEdit(null); setIsDeviceModalOpen(true); };
    const handleOpenEditDeviceModal = (device) => { setDeviceToEdit(device); setIsDeviceModalOpen(true); };
    const refreshDevices = () => { setActiveTab(''); setTimeout(() => setActiveTab('devices'), 10); };

    // --- IMPERSONATION HANDLERS ---
    
    // 1. Opens the confirmation modal
    const handleInitiateImpersonation = (targetUser) => {
        setImpersonateTarget(targetUser);
    };

    // 2. Executes the API call and session swap
    const executeImpersonation = async () => {
        if (!impersonateTarget) return;

        const toastId = toast.loading(`Switching to ${impersonateTarget.first_name}...`);
        
        try {
            const data = await api.impersonateUser(impersonateTarget.id);
            
            // A. Stash Current Admin Tokens
            const currentToken = localStorage.getItem('authToken');
            const currentRefreshToken = localStorage.getItem('refreshToken');
            
            if (currentToken) localStorage.setItem('adminAuthToken', currentToken);
            if (currentRefreshToken) localStorage.setItem('adminRefreshToken', currentRefreshToken);

            // B. Set Target User Tokens
            localStorage.setItem('authToken', data.accessToken);
            localStorage.setItem('refreshToken', data.refreshToken);
            
            toast.success(`Welcome, ${impersonateTarget.first_name}!`, { id: toastId });

            // C. Reload to re-initialize app permissions
            setTimeout(() => {
                window.location.href = '/'; 
            }, 800);

        } catch (error) {
            console.error(error);
            toast.error(error.message || "Failed to impersonate", { id: toastId });
            setImpersonateTarget(null); // Close modal on error
        }
    };

    const handleSaveDevice = async (deviceData) => {
        const savePromise = deviceToEdit ? api.updateDevice(deviceToEdit.id, deviceData) : api.createDevice(deviceData);
        toast.promise(savePromise, {
            loading: 'Saving device...',
            success: () => { setIsDeviceModalOpen(false); setDeviceToEdit(null); refreshDevices(); return `Device ${deviceToEdit ? 'updated' : 'created'} successfully!`; },
            error: (err) => `Error: ${err.message}`,
        });
    };

    const handleDeviceMenuAction = (type, device) => { setDeviceActionState({ type, device }); setOpenDeviceActionMenu(null); };
    const closeDeviceActionModal = () => setDeviceActionState({ type: null, device: null });
    const handleDeviceAction = async (actionType, deviceId, data = {}) => {
        setIsSubmittingDeviceAction(true);
        const actionPromise = api[actionType](deviceId, data);
        toast.promise(actionPromise, {
            loading: 'Sending command...',
            success: (response) => { closeDeviceActionModal(); return response.message || 'Action completed!'; },
            error: (err) => `Error: ${err.message}`,
        });
        try { await actionPromise; } catch (error) { /* Handled by toast */ }
        finally { setIsSubmittingDeviceAction(false); }
    };

    const sortedAndFilteredProperties = useMemo(() => {
        if (!properties) return [];
        const filtered = properties.filter(prop => (prop.name && prop.name.toLowerCase().includes(searchTerm.toLowerCase())) || (prop.code && prop.code.toLowerCase().includes(searchTerm.toLowerCase())));
        const sortable = [...filtered];
        sortable.sort((a, b) => {
            switch (sortOrder) {
                case 'last_created': return new Date(b.created_at) - new Date(a.created_at);
                case 'last_updated': const dateA = a.updated_at || a.created_at; const dateB = b.updated_at || b.created_at; return new Date(dateB) - new Date(dateA);
                case 'status': if (a.is_active === b.is_active) return a.name.localeCompare(b.name); return b.is_active - a.is_active;
                default: return a.name.localeCompare(b.name);
            }
        });
        return sortable;
    }, [searchTerm, properties, sortOrder]);

    const formatAddress = (prop) => {
        if (!prop) return '';
        return [prop.address, prop.city, prop.state, prop.zip_code].filter(part => part).join(', ');
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US');
    };

    if (isLoading) return <div className="page-container"><p>Loading properties...</p></div>;
    if (error && !isPropertyModalOpen) return <div className="page-container"><div className="error-message">{error}</div></div>;

    return (
        <div className="page-container">
            <div className="page-header"><h1 className="page-title">Properties</h1></div>
            <div className={`master-detail-layout ${selectedProperty ? 'detail-view-active' : ''}`}>
                <div className="master-column">
                    <div className="master-column-header">
                        <div className="search-input-container">
                            <Search size={18} className="search-icon" />
                            <input type="text" placeholder="Search properties..." className="search-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                        </div>
                        {canManageProperties && (<button onClick={handleOpenAddPropertyModal} className="action-button"><PlusCircle size={18} /><span>Add Property</span></button>)}
                    </div>
                    <div className="sort-container">
                        <label htmlFor="property-sort" className="sort-label">Sort by</label>
                        <select id="property-sort" className="sort-select" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
                            <option value="last_created">Last Created</option>
                            <option value="last_updated">Last Updated</option>
                            <option value="status">Active Status</option>
                            <option value="name_asc">Name (A-Z)</option>
                        </select>
                    </div>
                    <div className="property-list">
                        {(sortedAndFilteredProperties || []).map(prop => (
                            <div key={prop.id} className={`property-list-item ${selectedProperty?.id === prop.id ? 'selected' : ''}`} onClick={() => handleSelectProperty(prop)}>
                                <div className="property-list-item-header">
                                    <span className={`property-status-dot ${prop.is_active ? 'active' : 'inactive'}`}></span>
                                    <span className="property-list-item-name">{prop.name}</span>
                                </div>
                                <span className="property-list-item-code">{prop.code}</span>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="detail-column">
                    {selectedProperty ? (
                        <div className="card detail-card">
                            <div className="page-header-detail">
                                <a href="# " className="back-link mobile-only-back-link" onClick={(e) => { e.preventDefault(); setSelectedProperty(null); }}>←</a>
                                <h2 className="detail-title">{selectedProperty.name}</h2>
                                {canViewActions && (<div className="action-menu-container"><button className="icon-button" title="More actions" onClick={() => setOpenActionMenu(openActionMenu === selectedProperty.id ? null : selectedProperty.id)}><MoreVertical size={18} /></button>{openActionMenu === selectedProperty.id && (<div className="action-menu-dropdown">{canDownload && (<button onClick={() => { handleDownloadAll(selectedProperty.code); setOpenActionMenu(null); }}><Download size={16} /><span>Download All</span></button>)}{canManageProperties && (<><button onClick={() => { handleOpenServiceModal(selectedProperty); setOpenActionMenu(null); }}><DollarSign size={16} /><span>Add Service</span></button><button onClick={() => { handleOpenEditPropertyModal(selectedProperty); setOpenActionMenu(null); }}><Edit size={16} /><span>Edit Property</span></button></>)}</div>)}</div>)}
                            </div>
                            
                            <div className="detail-tabs">
                                <button className={`tab-button ${activeTab === 'details' ? 'active' : ''}`} onClick={() => setActiveTab('details')}>Details</button>
                                <button className={`tab-button ${activeTab === 'workforce' ? 'active' : ''}`} onClick={() => setActiveTab('workforce')}>Workforce</button>
                                <button className={`tab-button ${activeTab === 'devices' ? 'active' : ''}`} onClick={() => setActiveTab('devices')}>Devices</button>
                                {canManageSettings && (
                                    <button className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>Settings</button>
                                )}
                            </div>

                            <div className="tab-content">
                                {activeTab === 'details' && (<div className="detail-content"><div className="detail-item"><span className="detail-label">Code</span><span className="detail-value">{selectedProperty.code}</span></div><div className="detail-item"><span className="detail-label">Address</span><span className="detail-value">{formatAddress(selectedProperty)}</span></div><div className="detail-item"><span className="detail-label">Website</span><span className="detail-value">{selectedProperty.domain ? <a href={`http://${selectedProperty.domain}`} target="_blank" rel="noopener noreferrer">{selectedProperty.domain}</a> : 'N/A'}</span></div><div className="detail-item"><span className="detail-label">Property Email</span><span className="detail-value">{selectedProperty.email ? <a href={`mailto:${selectedProperty.email}`}>{selectedProperty.email}</a> : 'N/A'}</span></div><div className="detail-item"><span className="detail-label">Status</span><span className="detail-value"><span className={`status-badge ${selectedProperty.is_active ? 'active' : 'inactive'}`}>{selectedProperty.is_active ? 'Active' : 'Inactive'}</span></span></div><div className="detail-item"><span className="detail-label">Created Date</span><span className="detail-value">{formatDate(selectedProperty.created_at)}</span></div></div>)}
                                
                                {activeTab === 'workforce' && (isWorkforceLoading ? <p>Loading workforce...</p> : (
                                    <div className="property-group-content1">
                                        <div className="tab-content-header">
                                            <h3 className="tab-content-title">Workforce</h3>
                                            {canAddEmployee && (<button onClick={handleOpenAddEmployeeModal} className="action-button-small"><UserPlus size={16} /><span>Add Person</span></button>)}
                                        </div>
                                        {/* Pass the impersonation handler to all RoleSections */}
                                        <RoleSection title="Owners" people={workforceData.Owner} onPersonClick={handleOpenEditEmployeeModal} currentUser={user} onImpersonate={handleInitiateImpersonation}/>
                                        <RoleSection title="Managers" people={workforceData.Manager} onPersonClick={handleOpenEditEmployeeModal} currentUser={user} onImpersonate={handleInitiateImpersonation}/>
                                        <RoleSection title="Accountants" people={workforceData.Accountant} onPersonClick={handleOpenEditEmployeeModal} currentUser={user} onImpersonate={handleInitiateImpersonation}/>
                                        <RoleSection title="Housekeepers" people={workforceData.Housekeeper} onPersonClick={handleOpenEditEmployeeModal} currentUser={user} onImpersonate={handleInitiateImpersonation}/>
                                    </div>
                                ))}
                                
                                {activeTab === 'devices' && (isDevicesLoading ? <p>Loading devices...</p> : (<div className="devices-content"><div className="tab-content-header"><h3 className="tab-content-title">Devices</h3>{canManageProperties && (<button onClick={handleOpenAddDeviceModal} className="action-button-small"><PlusCircle size={16} /><span>Add Device</span></button>)}</div><div className="devices-grid">{(devices || []).map(device => (<div key={device.id} className="device-card"><span className={`status-dot ${device.is_active ? 'active' : 'inactive'}`}></span><div className="device-info"><span className="device-serial">{device.serial_number}</span><span className="device-name">{device.name || 'Unnamed Device'}</span></div><div className="device-actions">{canManageProperties && (<button className="icon-button" title="Edit Device" onClick={() => handleOpenEditDeviceModal(device)}><Edit size={18} /></button>)}<div className="action-menu-container"><button className="icon-button" title="More actions" onClick={() => setOpenDeviceActionMenu(openDeviceActionMenu === device.id ? null : device.id)}><MoreVertical size={18} /></button>{openDeviceActionMenu === device.id && (<div className="action-menu-dropdown"><button onClick={() => handleDeviceMenuAction('rebootDevice', device)}>Reboot Device</button>{canManageProperties && (<button onClick={() => handleDeviceMenuAction('setDeviceWifi', device)}>Set Wi-Fi</button>)}<button onClick={() => handleDeviceMenuAction('setDeviceTime', device)}>Set Time</button><button onClick={() => handleDeviceMenuAction('setDeviceAccessTimeZone', device)}>Set Access Time</button>{canManageProperties && (<button onClick={() => handleDeviceMenuAction('setNtpSettings', device)}>NTP Settings</button>)}<button onClick={() => handleDeviceMenuAction('addEmployeeByFingerprint', device)}>Add Employee (FP)</button><button onClick={() => handleDeviceMenuAction('addEmployeeByFace', device)}>Add Employee (Face)</button></div>)}</div></div></div>))}</div></div>))}
                                
                                {activeTab === 'settings' && canManageSettings && (
                                    <PropertySettingsTab 
                                        property={selectedProperty} 
                                        onUpdate={fetchProperties} 
                                    />
                                )}
                            </div>
                        </div>
                    ) : (<div className="placeholder-text">Select a property from the list to see its details.</div>)}
                </div>
            </div>
            
            {/* Existing Modals */}
            {isPropertyModalOpen && <PropertyModal isOpen={isPropertyModalOpen} onClose={() => setIsPropertyModalOpen(false)} onSubmit={handleSaveProperty} apiError={error} properties={properties} propertyToEdit={propertyToEdit} />}
            {serviceModalState.property && <AddServiceModal isOpen={serviceModalState.isOpen} onClose={() => setServiceModalState({ isOpen: false, property: null })} property={serviceModalState.property} onSave={handleAddService} />}
            {isEditEmployeeModalOpen && selectedPerson && (<EditEmployeeModal user={user} person={selectedPerson} onClose={() => setIsEditEmployeeModalOpen(false)} onEmployeeUpdated={refreshWorkforce} propertyId={selectedProperty.id} />)}
            {isAddEmployeeModalOpen && (<AddEmployeeModal user={user} initialPropertyId={selectedProperty.id} onClose={() => setIsAddEmployeeModalOpen(false)} onEmployeeAdded={refreshWorkforce} />)}
            {isDeviceModalOpen && (<DeviceModal isOpen={isDeviceModalOpen} properties={deviceToEdit ? properties : [selectedProperty]} deviceToEdit={deviceToEdit} devices={devices} onClose={() => { setIsDeviceModalOpen(false); setDeviceToEdit(null); }} onSubmit={handleSaveDevice} initialPropertyId={selectedProperty.id} />)}
            {deviceActionState.type === 'rebootDevice' && (<RebootModal device={deviceActionState.device} onClose={closeDeviceActionModal} isSubmitting={isSubmittingDeviceAction} onConfirm={() => handleDeviceAction('rebootDevice', deviceActionState.device.id)} />)}
            {deviceActionState.type === 'setDeviceWifi' && (<WiFiConfigModal device={deviceActionState.device} onClose={closeDeviceActionModal} isSubmitting={isSubmittingDeviceAction} onSubmit={(data) => handleDeviceAction('setDeviceWifi', deviceActionState.device.id, data)} />)}
            {deviceActionState.type === 'setDeviceTime' && (<SetTimeModal device={deviceActionState.device} onClose={closeDeviceActionModal} isSubmitting={isSubmittingDeviceAction} onSubmit={(data) => handleDeviceAction('setDeviceTime', deviceActionState.device.id, data)} />)}
            {deviceActionState.type === 'setDeviceAccessTimeZone' && (<AccessTimeModal device={deviceActionState.device} onClose={closeDeviceActionModal} isSubmitting={isSubmittingDeviceAction} onSubmit={(data) => handleDeviceAction('setDeviceAccessTimeZone', deviceActionState.device.id, data)} />)}
            {deviceActionState.type === 'setNtpSettings' && (<NtpSettingsModal device={deviceActionState.device} onClose={closeDeviceActionModal} isSubmitting={isSubmittingDeviceAction} onSubmit={(data) => handleDeviceAction('setNtpSettings', deviceActionState.device.id, data)} />)}
            {deviceActionState.type === 'addEmployeeByFingerprint' && (<AddEmployeeByFPModal device={deviceActionState.device} onClose={closeDeviceActionModal} isSubmitting={isSubmittingDeviceAction} onComplete={refreshWorkforce} onSubmit={(data) => handleDeviceAction('addEmployeeByFingerprint', deviceActionState.device.id, data)} />)}
            {deviceActionState.type === 'addEmployeeByFace' && (<AddEmployeeByFaceModal device={deviceActionState.device} onClose={closeDeviceActionModal} onComplete={refreshWorkforce} />)}

            {/* --- NEW: IMPERSONATION CONFIRMATION MODAL --- */}
            {impersonateTarget && (
                <div className="modal-backdrop">
                    <div className="modal-content" style={{ maxWidth: '450px' }}>
                        <div style={{ marginBottom: '20px' }}>
                            <div style={{ 
                                width: '50px', height: '50px', borderRadius: '50%', 
                                background: '#eff6ff', color: '#1798c1', 
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                margin: '0 auto 15px auto'
                            }}>
                                <LogIn size={28} />
                            </div>
                            <h2 style={{ textAlign: 'center', marginBottom: '10px' }}>Login as {impersonateTarget.first_name}?</h2>
                            <p style={{ textAlign: 'center', color: '#64748b', lineHeight: '1.5' }}>
                                You are about to access the system as <strong>{impersonateTarget.first_name} {impersonateTarget.last_name}</strong>. 
                                <br/><br/>
                                You will see exactly what they see. Your Admin session will be paused.
                            </p>
                        </div>
                        <div className="modal-actions" style={{ justifyContent: 'center', gap: '15px' }}>
                            <button 
                                className="button button-secondary" 
                                onClick={() => setImpersonateTarget(null)}
                            >
                                Cancel
                            </button>
                            <button 
                                className="button action-button" 
                                onClick={executeImpersonation}
                                style={{ backgroundColor: '#0f172a' }} // Dark button
                            >
                                Yes, Login as User
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PropertiesScreen;